import React, { useState, useEffect, useRef, useCallback } from 'react';

const LagosWasteMap = () => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowsRef = useRef([]);
  const trafficLayerRef = useRef(null);
  const scriptLoadedRef = useRef(false);
  
  const [activeFilter, setActiveFilter] = useState('all-sites');
  const [isLoading, setIsLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);

  // Lagos dumpsites data
  const dumpsites = [
    {
      id: 1,
      name: "Ajegunle Collection Point",
      location: "Roofseller's Football Field, Ajegunle",
      coordinates: { lat: 6.4698, lng: 3.3528 },
      status: "Active",
      wasteTypes: ["Organic", "Plastic", "Metal", "Textile"],
      capacity: 5000,
      currentLoad: 3200,
      revenue: 1250000,
      district: "Lagos Mainland"
    },
    {
      id: 2,
      name: "Ebute Meta Processing Hub",
      location: "Methodist Primary School Area",
      coordinates: { lat: 6.4889, lng: 3.3742 },
      status: "Re-registered",
      wasteTypes: ["Plastic", "Metal"],
      capacity: 3000,
      currentLoad: 2800,
      revenue: 980000,
      district: "Lagos Island"
    },
    {
      id: 3,
      name: "Lekki Green Garden Center",
      location: "Sahara Green Garden Ground",
      coordinates: { lat: 6.4281, lng: 3.5186 },
      status: "Active",
      wasteTypes: ["Industrial", "Metal", "Textile"],
      capacity: 8000,
      currentLoad: 4500,
      revenue: 3200000,
      district: "Victoria Island/Lekki"
    },
    {
      id: 4,
      name: "Apapa Industrial Zone",
      location: "Apapa Port Complex",
      coordinates: { lat: 6.4406, lng: 3.3594 },
      status: "Industrial",
      wasteTypes: ["Industrial", "Metal", "Electronic"],
      capacity: 12000,
      currentLoad: 8200,
      revenue: 5400000,
      district: "Apapa"
    },
    {
      id: 5,
      name: "Ikeja Collection Center",
      location: "Computer Village Area",
      coordinates: { lat: 6.6018, lng: 3.3515 },
      status: "Pending",
      wasteTypes: ["Electronic", "Plastic", "Metal"],
      capacity: 4000,
      currentLoad: 1200,
      revenue: 450000,
      district: "Ikeja"
    },
    {
      id: 6,
      name: "Ikoyi Waste Management",
      location: "Ikoyi Club Area",
      coordinates: { lat: 6.4541, lng: 3.4316 },
      status: "Active",
      wasteTypes: ["Organic", "Plastic"],
      capacity: 3500,
      currentLoad: 2100,
      revenue: 1800000,
      district: "Lagos Island"
    },
    {
      id: 7,
      name: "Surulere Community Hub",
      location: "National Stadium Area",
      coordinates: { lat: 6.4969, lng: 3.3664 },
      status: "Industrial",
      wasteTypes: ["Industrial", "Textile", "Metal"],
      capacity: 6000,
      currentLoad: 4200,
      revenue: 2900000,
      district: "Lagos Mainland"
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      'Active': '#10b981',
      'Re-registered': '#ef4444',
      'Pending': '#f59e0b',
      'Industrial': '#3b82f6'
    };
    return colors[status] || '#6b7280';
  };

  const getWasteTypeClass = (type) => {
    const classes = {
      'Organic': 'bg-green-100 text-green-800',
      'Plastic': 'bg-yellow-100 text-yellow-800',
      'Metal': 'bg-red-100 text-red-800',
      'Textile': 'bg-blue-100 text-blue-800',
      'Industrial': 'bg-indigo-100 text-indigo-800',
      'Electronic': 'bg-purple-100 text-purple-800'
    };
    return classes[type] || 'bg-gray-100 text-gray-800';
  };

  const createInfoWindowContent = (site) => {
    const utilizationPercent = ((site.currentLoad / site.capacity) * 100).toFixed(1);
    const statusColor = getStatusColor(site.status);
    
    return `
      <div class="max-w-xs p-2">
        <div class="flex items-center gap-2 mb-2">
          <div class="w-2 h-2 rounded-full" style="background-color: ${statusColor}"></div>
          <div class="font-semibold text-sm text-gray-900">${site.name}</div>
        </div>
        <div class="text-xs text-gray-600 mb-2">${site.location}</div>
        <div class="grid grid-cols-2 gap-2 mb-2">
          <div class="text-xs">
            <span class="text-gray-500 block">Capacity</span>
            <span class="font-semibold text-gray-900">${site.capacity.toLocaleString()} tons</span>
          </div>
          <div class="text-xs">
            <span class="text-gray-500 block">Utilization</span>
            <span class="font-semibold text-gray-900">${utilizationPercent}%</span>
          </div>
          <div class="text-xs">
            <span class="text-gray-500 block">Revenue</span>
            <span class="font-semibold text-gray-900">₦${(site.revenue/1000000).toFixed(1)}M</span>
          </div>
          <div class="text-xs">
            <span class="text-gray-500 block">District</span>
            <span class="font-semibold text-gray-900">${site.district}</span>
          </div>
        </div>
        <div class="flex flex-wrap gap-1">
          ${site.wasteTypes.map(type => `<span class="px-2 py-1 rounded text-xs font-medium ${getWasteTypeClass(type)}">${type}</span>`).join('')}
        </div>
      </div>
    `;
  };

  const cleanupMap = useCallback(() => {
    // Close and clear info windows
    infoWindowsRef.current.forEach(infoWindow => {
      try {
        if (infoWindow) {
          infoWindow.close();
        }
      } catch (e) {
        // Ignore cleanup errors
      }
    });
    infoWindowsRef.current = [];

    // Clear markers
    markersRef.current.forEach(({ marker }) => {
      try {
        if (marker) {
          marker.setMap(null);
        }
      } catch (e) {
        // Ignore cleanup errors
      }
    });
    markersRef.current = [];

    // Clear traffic layer
    if (trafficLayerRef.current) {
      try {
        trafficLayerRef.current.setMap(null);
      } catch (e) {
        // Ignore cleanup errors
      }
      trafficLayerRef.current = null;
    }

    // Clear map instance
    mapInstanceRef.current = null;
  }, []);

  const initializeMap = useCallback(() => {
    if (!window.google || !mapRef.current) return;

    const lagosCenter = { lat: 6.5244, lng: 3.3792 };
    
    try {
      // Clean up any existing map
      cleanupMap();

      const mapInstance = new window.google.maps.Map(mapRef.current, {
        zoom: 11,
        center: lagosCenter,
        mapTypeId: 'roadmap',
        styles: [
          {
            featureType: 'water',
            elementType: 'geometry',
            stylers: [{ color: '#e9e9e9' }, { lightness: 17 }]
          },
          {
            featureType: 'landscape',
            elementType: 'geometry',
            stylers: [{ color: '#f5f5f5' }, { lightness: 20 }]
          }
        ]
      });

      mapInstanceRef.current = mapInstance;

      // Create markers
      const newMarkers = dumpsites.map(site => {
        const marker = new window.google.maps.Marker({
          position: site.coordinates,
          map: mapInstance,
          title: site.name,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: getStatusColor(site.status),
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
            scale: 8
          },
          animation: window.google.maps.Animation.DROP
        });

        const infoWindow = new window.google.maps.InfoWindow({
          content: createInfoWindowContent(site)
        });

        marker.addListener('click', () => {
          // Close all other info windows
          infoWindowsRef.current.forEach(iw => {
            try {
              if (iw) iw.close();
            } catch (e) {
              // Ignore errors
            }
          });
          infoWindow.open(mapInstance, marker);
        });

        infoWindowsRef.current.push(infoWindow);
        return { marker, infoWindow, site };
      });

      markersRef.current = newMarkers;
      setIsLoading(false);
      setMapReady(true);

      // Create traffic layer
      const traffic = new window.google.maps.TrafficLayer();
      trafficLayerRef.current = traffic;

    } catch (error) {
      console.error('Error initializing map:', error);
      setIsLoading(false);
    }
  }, [cleanupMap, dumpsites]);

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    
    if (!mapInstanceRef.current || markersRef.current.length === 0) return;

    try {
      switch (filter) {
        case 'all-sites':
          markersRef.current.forEach(({ marker }) => {
            if (marker) marker.setVisible(true);
          });
          mapInstanceRef.current.setMapTypeId('roadmap');
          if (trafficLayerRef.current && trafficLayerRef.current.getMap()) {
            trafficLayerRef.current.setMap(null);
          }
          break;
        case 'active-only':
          markersRef.current.forEach(({ marker, site }) => {
            if (marker) marker.setVisible(site.status === 'Active');
          });
          mapInstanceRef.current.setMapTypeId('roadmap');
          if (trafficLayerRef.current && trafficLayerRef.current.getMap()) {
            trafficLayerRef.current.setMap(null);
          }
          break;
        case 'satellite-view':
          markersRef.current.forEach(({ marker }) => {
            if (marker) marker.setVisible(true);
          });
          mapInstanceRef.current.setMapTypeId('satellite');
          if (trafficLayerRef.current && trafficLayerRef.current.getMap()) {
            trafficLayerRef.current.setMap(null);
          }
          break;
        case 'traffic-view':
          markersRef.current.forEach(({ marker }) => {
            if (marker) marker.setVisible(true);
          });
          mapInstanceRef.current.setMapTypeId('roadmap');
          if (trafficLayerRef.current) {
            if (trafficLayerRef.current.getMap()) {
              trafficLayerRef.current.setMap(null);
            } else {
              trafficLayerRef.current.setMap(mapInstanceRef.current);
            }
          }
          break;
      }
    } catch (error) {
      console.error('Error handling filter change:', error);
    }
  };

  const loadGoogleMapsScript = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (window.google && window.google.maps) {
        resolve();
        return;
      }

      if (scriptLoadedRef.current) return;

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=YOUR_API_KEY&libraries=geometry`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        scriptLoadedRef.current = true;
        resolve();
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }, []);

  useEffect(() => {
    loadGoogleMapsScript()
      .then(() => {
        if (mapRef.current) {
          initializeMap();
        }
      })
      .catch(error => {
        console.error('Error loading Google Maps:', error);
        setIsLoading(false);
      });

    // Cleanup on unmount
    return () => {
      cleanupMap();
    };
  }, [loadGoogleMapsScript, initializeMap, cleanupMap]);

  const getStatusCount = (status) => {
    return dumpsites.filter(site => site.status === status).length;
  };

  return (
    <div className="w-full h-full relative">
      <div className="h-full">
        <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-3 p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Lagos Dumpsite Locations</h3>
          <div className="flex gap-3 flex-wrap justify-center">
            <button
              onClick={() => handleFilterChange('all-sites')}
              className={`px-4 py-2 border rounded-md text-sm transition-all ${
                activeFilter === 'all-sites'
                  ? 'bg-emerald-500 text-white border-emerald-500'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              All Sites
            </button>
            <button
              onClick={() => handleFilterChange('active-only')}
              className={`px-4 py-2 border rounded-md text-sm transition-all ${
                activeFilter === 'active-only'
                  ? 'bg-emerald-500 text-white border-emerald-500'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Active Only
            </button>
            <button
              onClick={() => handleFilterChange('satellite-view')}
              className={`px-4 py-2 border rounded-md text-sm transition-all ${
                activeFilter === 'satellite-view'
                  ? 'bg-emerald-500 text-white border-emerald-500'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Satellite
            </button>
            <button
              onClick={() => handleFilterChange('traffic-view')}
              className={`px-4 py-2 border rounded-md text-sm transition-all ${
                activeFilter === 'traffic-view'
                  ? 'bg-emerald-500 text-white border-emerald-500'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Traffic
            </button>
          </div>
        </div>
        
        <div className="relative flex-1">
          <div
            ref={mapRef}
            className={`w-full h-full ${
              isLoading ? 'flex items-center justify-center bg-gray-50 text-gray-500' : ''
            }`}
          >
            {isLoading && (
              <div className="flex items-center gap-2">
                <div className="animate-spin w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full"></div>
                Loading Lagos Map...
              </div>
            )}
          </div>
          
          {mapReady && (
            <div className="absolute top-2 right-2 bg-white p-3 rounded-lg shadow-lg min-w-[160px] z-10 border">
              <div className="text-xs font-semibold mb-2 text-gray-900">Site Status</div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-xs text-gray-700">
                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                  <span>Active ({getStatusCount('Active')})</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-700">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  <span>Re-registered ({getStatusCount('Re-registered')})</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-700">
                  <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                  <span>Pending ({getStatusCount('Pending')})</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-700">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span>Industrial ({getStatusCount('Industrial')})</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LagosWasteMap;